import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { 
  GitCompare, Loader2, Check, X, BarChart3, 
  Navigation, Timer, Zap, Activity, TrendingUp
} from 'lucide-react';
import { MapContainer, TileLayer, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

// Fit map to all routes
function FitAllBounds({ routes }) {
  const map = useMap();
  
  useEffect(() => {
    if (routes && routes.length > 0) {
      const allPoints = routes.flatMap(r => r.points.map(p => [p.lat, p.lng]));
      if (allPoints.length > 0) {
        const bounds = L.latLngBounds(allPoints);
        map.fitBounds(bounds, { padding: [20, 20] });
      }
    }
  }, [map, routes]);
  
  return null;
}

export default function GPSComparison({ childId, sessions = [] }) {
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSelector, setShowSelector] = useState(false);

  const handleToggleSession = (sessionId) => {
    if (selectedSessions.includes(sessionId)) {
      setSelectedSessions(selectedSessions.filter(id => id !== sessionId));
    } else if (selectedSessions.length < 5) {
      setSelectedSessions([...selectedSessions, sessionId]);
    } else {
      toast.error('Maximum 5 sessions can be compared');
    }
  };

  const handleCompare = async () => {
    if (selectedSessions.length < 2) {
      toast.error('Select at least 2 sessions to compare');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/gps-tracking/compare`, {
        session_ids: selectedSessions
      });
      setComparison(response.data);
      setShowSelector(false);
    } catch (error) {
      toast.error('Failed to compare sessions');
    } finally {
      setLoading(false);
    }
  };

  const formatDistance = (meters) => {
    if (meters >= 1000) return `${(meters / 1000).toFixed(2)} km`;
    return `${Math.round(meters)} m`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    if (mins >= 60) {
      const hours = Math.floor(mins / 60);
      return `${hours}h ${mins % 60}m`;
    }
    return `${mins}m`;
  };

  // Get max value for each metric for bar chart scaling
  const getMaxValue = (metric) => {
    if (!comparison) return 0;
    const values = comparison.metrics_comparison[metric];
    return Math.max(...values.map(v => v.value));
  };

  return (
    <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6" data-testid="gps-comparison">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <GitCompare className="h-6 w-6 text-purple-400" />
          <h2 className="text-lg font-bold uppercase tracking-widest text-white">Session Comparison</h2>
        </div>
        <Button
          onClick={() => setShowSelector(!showSelector)}
          className="h-9 px-4 bg-purple-600 hover:bg-purple-500 text-white text-xs"
          data-testid="compare-sessions-button"
        >
          <GitCompare className="h-4 w-4 mr-2" />
          Compare Sessions
        </Button>
      </div>

      {/* Session Selector */}
      {showSelector && (
        <div className="mb-6 p-4 bg-zinc-900/50 border border-zinc-700 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold text-zinc-400 uppercase">
              Select Sessions ({selectedSessions.length}/5)
            </span>
            <div className="flex gap-2">
              <Button
                onClick={() => setSelectedSessions([])}
                variant="outline"
                className="h-8 px-3 border-zinc-700 text-zinc-400 text-xs"
              >
                Clear
              </Button>
              <Button
                onClick={handleCompare}
                disabled={loading || selectedSessions.length < 2}
                className="h-8 px-4 bg-purple-600 hover:bg-purple-500 text-white text-xs"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Compare'}
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
            {sessions.map((session) => {
              const isSelected = selectedSessions.includes(session.id);
              return (
                <div
                  key={session.id}
                  onClick={() => handleToggleSession(session.id)}
                  className={`flex items-center gap-3 p-3 rounded cursor-pointer transition-colors ${
                    isSelected 
                      ? 'bg-purple-600/20 border border-purple-500' 
                      : 'bg-zinc-800/50 border border-transparent hover:bg-zinc-800'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    isSelected ? 'border-purple-500 bg-purple-500' : 'border-zinc-600'
                  }`}>
                    {isSelected && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white capitalize truncate">
                      {session.session_type} - {session.sport}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {new Date(session.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit', month: 'short'
                      })} • {formatDistance(session.summary?.total_distance || 0)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Comparison Results */}
      {comparison && (
        <div className="space-y-6">
          {/* Route Map Overlay */}
          {comparison.routes?.length > 0 && (
            <div className="rounded-lg overflow-hidden">
              <div className="flex items-center justify-between p-3 bg-zinc-800/50">
                <span className="text-sm font-bold text-white uppercase">Route Overlay</span>
                <div className="flex gap-2">
                  {comparison.sessions.map((session, i) => (
                    <div key={session.id} className="flex items-center gap-1">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: session.color }}
                      />
                      <span className="text-xs text-zinc-400 capitalize">
                        {session.session_type}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <MapContainer
                center={[51.5074, -0.1278]}
                zoom={15}
                style={{ height: '300px', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitAllBounds routes={comparison.routes} />
                {comparison.routes.map((route) => (
                  <Polyline
                    key={route.session_id}
                    positions={route.points.map(p => [p.lat, p.lng])}
                    pathOptions={{
                      color: route.color,
                      weight: 4,
                      opacity: 0.8
                    }}
                  />
                ))}
              </MapContainer>
            </div>
          )}

          {/* Metrics Comparison Bars */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Total Distance */}
            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Navigation className="h-4 w-4 text-[#02b5ff]" />
                <span className="text-sm font-bold text-white">Distance</span>
              </div>
              <div className="space-y-2">
                {comparison.metrics_comparison.total_distance.map((m, i) => (
                  <div key={m.session_id} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: m.color }}
                    />
                    <div className="flex-1 h-6 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ 
                          width: `${m.percent_of_max || 0}%`,
                          backgroundColor: m.color
                        }}
                      />
                    </div>
                    <span className="text-sm font-mono text-white w-20 text-right">
                      {formatDistance(m.value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Duration */}
            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Timer className="h-4 w-4 text-green-400" />
                <span className="text-sm font-bold text-white">Duration</span>
              </div>
              <div className="space-y-2">
                {comparison.metrics_comparison.duration.map((m) => {
                  const maxDuration = getMaxValue('duration');
                  const percent = maxDuration > 0 ? (m.value / maxDuration * 100) : 0;
                  return (
                    <div key={m.session_id} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: m.color }}
                      />
                      <div className="flex-1 h-6 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ width: `${percent}%`, backgroundColor: m.color }}
                        />
                      </div>
                      <span className="text-sm font-mono text-white w-16 text-right">
                        {formatDuration(m.value)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Avg Speed */}
            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="h-4 w-4 text-yellow-400" />
                <span className="text-sm font-bold text-white">Avg Speed (km/h)</span>
              </div>
              <div className="space-y-2">
                {comparison.metrics_comparison.avg_speed.map((m) => {
                  const maxSpeed = getMaxValue('avg_speed');
                  const percent = maxSpeed > 0 ? (m.value / maxSpeed * 100) : 0;
                  return (
                    <div key={m.session_id} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: m.color }}
                      />
                      <div className="flex-1 h-6 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ width: `${percent}%`, backgroundColor: m.color }}
                        />
                      </div>
                      <span className="text-sm font-mono text-white w-16 text-right">
                        {m.value}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Sprints */}
            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="h-4 w-4 text-orange-400" />
                <span className="text-sm font-bold text-white">Sprints</span>
              </div>
              <div className="space-y-2">
                {comparison.metrics_comparison.sprint_count.map((m) => {
                  const maxSprints = getMaxValue('sprint_count');
                  const percent = maxSprints > 0 ? (m.value / maxSprints * 100) : 0;
                  return (
                    <div key={m.session_id} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: m.color }}
                      />
                      <div className="flex-1 h-6 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ width: `${percent}%`, backgroundColor: m.color }}
                        />
                      </div>
                      <span className="text-sm font-mono text-white w-12 text-right">
                        {m.value}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Session Details Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left py-2 px-3 text-zinc-500 font-medium">Session</th>
                  <th className="text-right py-2 px-3 text-zinc-500 font-medium">Distance</th>
                  <th className="text-right py-2 px-3 text-zinc-500 font-medium">Duration</th>
                  <th className="text-right py-2 px-3 text-zinc-500 font-medium">Avg Speed</th>
                  <th className="text-right py-2 px-3 text-zinc-500 font-medium">Max Speed</th>
                  <th className="text-right py-2 px-3 text-zinc-500 font-medium">Sprints</th>
                </tr>
              </thead>
              <tbody>
                {comparison.sessions.map((session, i) => (
                  <tr key={session.id} className="border-b border-zinc-800/50">
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: session.color }}
                        />
                        <span className="text-white capitalize">{session.session_type}</span>
                        <span className="text-zinc-500 text-xs">
                          ({new Date(session.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })})
                        </span>
                      </div>
                    </td>
                    <td className="text-right py-2 px-3 font-mono text-white">
                      {formatDistance(session.summary?.total_distance || 0)}
                    </td>
                    <td className="text-right py-2 px-3 font-mono text-white">
                      {formatDuration(session.summary?.duration)}
                    </td>
                    <td className="text-right py-2 px-3 font-mono text-white">
                      {((session.summary?.avg_speed || 0) * 3.6).toFixed(1)} km/h
                    </td>
                    <td className="text-right py-2 px-3 font-mono text-white">
                      {((session.summary?.max_speed || 0) * 3.6).toFixed(1)} km/h
                    </td>
                    <td className="text-right py-2 px-3 font-mono text-white">
                      {session.summary?.sprint_count || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!comparison && !showSelector && (
        <div className="text-center py-8 border-t border-zinc-800">
          <GitCompare className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">Compare GPS sessions to track progress</p>
          <p className="text-zinc-600 text-xs mt-1">Select 2-5 sessions to see metrics side by side</p>
        </div>
      )}
    </div>
  );
}
